import {Button, Col, FloatingLabel, Form, Modal, Row} from "react-bootstrap";
import {AgGridReact} from "ag-grid-react";
import React, {Fragment, useEffect, useRef, useState} from "react";
import {getTracking, getTrackingTypes} from "../api/AdminApi";
import {TRACKING_TYPE_BEING_IMPERSONATED, TRACKING_TYPE_EMAIL_SENT, TRACKING_TYPE_IMPERSONATE_USER} from "../constants";
import {useLocation} from "react-router-dom";
import {toolTipRenderer} from "../utils/gridUtils";
import {dateTimeToStr, prettyPrintObject, titleCase} from "../utils/usacmUtils";
import {getErrorMessageForAllErrors} from "../utils/formUtils";


export function TrackingList() {
  const [trackingList, setTrackingList] = useState([]);
  const [trackingTypes, setTrackingTypes] = useState([]);
  const hasFewRows = (trackingList || []).length < 12;
  const gridRef = useRef();
  const [showingEmailDialog, setShowingEmailDialog] = useState(false);
  const [selectedTracking, setSelectedTracking] = useState(null);
  const [showAllConf, setShowAllConf] = useState(false);
  const {search} = useLocation();
  const searchParams = new URLSearchParams(search);
  const filterUserId = searchParams.get('userid') || '';
  const [showPageLoads, setShowPageLoads] = useState(false);
  const [dateFilterStart, setDateFilterStart] = useState('');
  const [dateFilterEnd, setDateFilterEnd] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [targetId, setTargetId] = useState('');
  const [pageNum, setPageNum] = useState('1');
  const [pageSize, setPageSize] = useState('1000');
  const [quickFilter, setQuickFilter] = useState('');
  const [errors, setErrors] = useState([]);

  function loadTrackingData() {
    setErrors([]);
    getTracking(showAllConf, filterUserId, showPageLoads, dateFilterStart, dateFilterEnd,
      typeFilter, targetId, pageNum, pageSize, '', '', (code, data, errors) => {
        if (code === 200) {
          setTrackingList(data);
        } else {
          setTrackingList([]);
          setErrors(errors);
        }
      });
  }

  useEffect(() => {
    getTrackingTypes((code, data, errors) => {
      if (code === 200) {
        setTrackingTypes(data);
      }
    });
  }, []);


  useEffect(() => {
    loadTrackingData();
  }, [filterUserId]);

  function showEmailDialog(trackingId) {
    const tracking = trackingList.find(t => t.id === trackingId);
    if (!tracking) {
      console.warn('Unable to find tracking with id ' + trackingId);
      return;
    }
    setSelectedTracking(tracking);
    setShowingEmailDialog(true);
  }

  function hideEmailDialog() {
    setSelectedTracking(null);
    setShowingEmailDialog(false);
  }

  function displayTrackingType(type) {
    return titleCase(type);
  }

  // Column setup
  const colDefs = [
    {
      field: 'create_date',
      headerName: 'Date (browser timezone)',
      minWidth: 180,
      flex: 1,
      // This will parse the GMT date string into a JS Date in the browser's timezone
      valueGetter: params => dateTimeToStr(new Date(Date.parse(params.data?.create_date))),
    },
    {
      headerName: 'Type',
      minWidth: 180,
      flex: 2,
      valueGetter: params => displayTrackingType(params.data?.type),
      cellRenderer: params => {
        const tracking = params.data;
        let type = tracking?.type;
        const typeDisplay = displayTrackingType(type);
        if (type === TRACKING_TYPE_EMAIL_SENT) {
          return <Fragment>
            {typeDisplay}
            <Button type="button" onClick={() => showEmailDialog(tracking.id)} size="sm" className="ms-2">Details</Button>
          </Fragment>
        }
        return typeDisplay;
      },
    },
    // Only show user if there might be multiple users
    ...(filterUserId ? [] : [
      {
        headerName: 'User',
        minWidth: 140,
        flex: 2,
        valueGetter: params => {
          const tracking = params.data;
          if (tracking.type === TRACKING_TYPE_EMAIL_SENT) {
            return "to: " + tracking.data.to;
          }
          return tracking.user_email;
        },
      }]),
    {
      headerName: 'Details',
      minWidth: 100,
      flex: 2,
      cellRenderer: toolTipRenderer,
      valueGetter: params => {
        const tracking = params.data;
        let retVal = '';
        if (tracking.type === TRACKING_TYPE_EMAIL_SENT) {
          return tracking.data?.subject;
        }
        if (tracking.type === TRACKING_TYPE_IMPERSONATE_USER) {
          return `Impersonating user ${tracking.data?.email} with id ${tracking.target_id} `;
        }
        if (tracking.type === TRACKING_TYPE_BEING_IMPERSONATED) {
          return `Being impersonated by ${tracking.data?.email}`;
        }
        if (tracking.target_id) {
          retVal += 'id=' + tracking.target_id + ' ';
        }
        if (tracking.data) {
          retVal += prettyPrintObject(tracking.data);
        }
        return retVal;
      },
    },
    ...(showAllConf ? [{field: 'conf', headerName: 'Conf'}] : []),
  ];

  const defaultColDefs = {
    sortable: false,
    filter: false,
    resizable: true,
  }

  function quickFilterChanged(newValue) {
    setQuickFilter(newValue);
    if (gridRef?.current?.api) {
      gridRef?.current?.api.setGridOption('quickFilterText', newValue);
    } else {
      console.warn('cannot set quick filter as api is not available');
    }
  }

  return (
    <div>
      {filterUserId &&
        <Row className="mb-3">
          <Col>
            Showing activity for user_id={filterUserId} {trackingList[0]?.user_email}
          </Col>
        </Row>
      }

      <Row className='mb-3'>
        <Col>
          <div className='d-flex flex-wrap row-gap-2'>
            <div className='ms-1 me-1'>
              <Form.Check
                type="checkbox"
                label="Show 'Me' Page Loads"
                id="show_me"
                checked={showPageLoads}
                onChange={e => setShowPageLoads(e.target.checked)}
                style={{maxWidth: '120px'}}
              />
            </div>

            <div className='ms-1 me-1'>
              <Form.Check
                type="checkbox"
                label="Show All Conf"
                id="show_all_conf"
                checked={showAllConf}
                onChange={e => setShowAllConf(e.target.checked)}
                style={{maxWidth: '120px'}}
              />
            </div>

            <div className="d-flex ms-1 me-1">
              <Form.Group controlId="date_filter_start" className="flex-grow-1 me-1">
                <FloatingLabel controlId="date_filter_start" label="Start">
                  <Form.Control type="date"
                                placeholder="Start"
                                name="date_filter_start"
                                value={dateFilterStart}
                                onChange={e => setDateFilterStart(e.target.value)}/>
                </FloatingLabel>
              </Form.Group>
              <Form.Group controlId="date_filter_end" className="flex-grow-1 ms-1">
                <FloatingLabel controlId="date_filter_end" label="End">
                  <Form.Control type="date"
                                placeholder="End"
                                name="date_filter_end"
                                value={dateFilterEnd}
                                onChange={e => setDateFilterEnd(e.target.value)}/>
                </FloatingLabel>
              </Form.Group>
            </div>

            <div className='ms-1 me-1'>
              <Form.Group controlId="type-filter">
                <Form.Control
                  className="form-select"
                  as="select"
                  value={typeFilter || ''}
                  onChange={e => setTypeFilter(e.target.value)}
                >
                  <option value='' key=''>Filter by Type...</option>
                  {trackingTypes?.map(type => {
                    return <option value={type} key={type}>{displayTrackingType(type)}</option>
                  })}
                </Form.Control>
              </Form.Group>
            </div>

            <div className='ms-1 me-1'>
              <Form.Group controlId="target_id">
                <FloatingLabel controlId="target_id" label="Target ID">
                  <Form.Control type="text"
                                placeholder=""
                                name="target_id"
                                style={{maxWidth: '100px'}}
                                value={targetId}
                                onChange={e => setTargetId(e.target.value)}/>
                </FloatingLabel>
              </Form.Group>
            </div>

            <div className='ms-1 me-1'>
              <Form.Group controlId="page_num">
                <FloatingLabel controlId="page_num" label="Pg #">
                  <Form.Control type="number"
                                step="1"
                                min="1"
                                placeholder="Pg #"
                                name="page_num"
                                style={{maxWidth: '80px'}}
                                value={pageNum}
                                onChange={e => setPageNum(e.target.value)}/>
                </FloatingLabel>
              </Form.Group>
            </div>

            <div className='ms-1 me-1'>
              <Form.Group controlId="page_size">
                <FloatingLabel controlId="page_size" label="Page Size">
                  <Form.Control type="number"
                                step="1000"
                                min="1"
                                placeholder="Page Size"
                                name="page_size"
                                style={{maxWidth: '100px'}}
                                value={pageSize}
                                onChange={e => setPageSize(e.target.value)}/>
                </FloatingLabel>
              </Form.Group>
            </div>

            <div className='ms-1 me-1'>
              <Button onClick={() => loadTrackingData()}>Apply Filters </Button>
            </div>
          </div>
        </Col>
      </Row>

      {errors && errors.length > 0 &&
        <Row className='mb-3'>
          <Col className="text-center mb-3 usacm-error-message">
            {getErrorMessageForAllErrors(errors)}
          </Col>
        </Row>
      }

      <Row className='mb-1'>
        <Col>
          <div className='flex-grow-1 ms-1'>
            <Form.Control type="text"
                          id="quick-filter"
                          placeholder='Search Results...'
                          name="quick-filter"
                          value={quickFilter}
                          onChange={e => quickFilterChanged(e.target.value)}
                          style={{minWidth: '150px'}}/>
          </div>
        </Col>
      </Row>

      <Row>
        <Col>
          <div className="ag-theme-alpine" style={{width: '100%', ...(hasFewRows ? {} : {height: '800px'})}}>
            <AgGridReact
              ref={gridRef}
              rowData={trackingList}
              columnDefs={colDefs}
              defaultColDef={defaultColDefs}
              gridOptions={{enableCellTextSelection: true, ensureDomOrder: true}} // enable cell selection
              domLayout={hasFewRows ? "autoHeight" : "normal"}
              getRowId={(params) => params.data.id} // prevent re-rendering the whole table when data changes
            />
          </div>
        </Col>
      </Row>

      <Row>
        <Col className='mt-3'>
          Note: The "Me" endpoint is called when the page is loaded.
        </Col>
      </Row>

      <Modal show={showingEmailDialog}
             onHide={() => hideEmailDialog()}
             size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Email Sent</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>
            <b>Date:</b> {selectedTracking?.create_date}
          </div>
          <div className="mt-2">
            <b>To:</b> {selectedTracking?.data?.to}
          </div>
          <div className="mt-2">
            <b>Subject:</b> {selectedTracking?.data?.subject}
          </div>
          <div className="mt-3" dangerouslySetInnerHTML={{__html: selectedTracking?.data?.body}}/>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => hideEmailDialog()}>Close</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );

}
